import { utcToZonedTime } from "date-fns-tz";
import { UTime } from "@/app/utils/UTime";

function getDay(dayIndex: number, lang: string): string {
  const days: Record<string, string[]> = {
    fr: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"],
    en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  };
  return days[lang]?.[dayIndex] ?? days.fr[dayIndex];
}

function getCalendarMonths(lang: string): string[] {
  const months: Record<string, string[]> = {
    fr: [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre",
    ],
    en: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
  };
  return months[lang] ?? months.fr;
}

export class UDate {
  public static toDayInput(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  public static toIsoString(value: Date | string | null | undefined): string | null {
    if (!value) {
      return null;
    }

    const date = value instanceof Date ? value : new Date(value);

    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }

  public static daysAgo(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() - days);

    return date;
  }

  public static toDate(date: string): Date {
    return new Date(date);
  }

  public static startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  public static subDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
  }

  public static getWeekNumber(date: Date): number {
    const currentDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayOfWeek = currentDate.getUTCDay() || 7;

    currentDate.setUTCDate(currentDate.getUTCDate() + 4 - dayOfWeek);

    const yearStart = new Date(Date.UTC(currentDate.getUTCFullYear(), 0, 1));

    return Math.ceil(((currentDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  }

  public static getWeekOfMonth(date: Date) {
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const firstDayOfWeek = firstDayOfMonth.getDay(); // 0 = dimanche, 1 = lundi, ..., 6 = samedi
    const dayOfMonth = date.getDate();

    // Trouver le premier lundi du mois
    const firstMondayOffset = (1 - firstDayOfWeek + 7) % 7; // Déterminer le décalage vers le premier lundi
    const firstMondayOfMonth = firstDayOfMonth.getDate() + firstMondayOffset;

    // Calculer le numéro de la semaine
    if (dayOfMonth < firstMondayOfMonth) {
      return 1; // Si la date est avant le premier lundi, elle est dans la première semaine
    }

    // Calculer le numéro de la semaine (commence à 1)
    return Math.ceil((dayOfMonth - firstMondayOfMonth + 1) / 7);
  }

  public static convertFieldsToDate<T>(obj: T, keys: string[]) {
    keys.forEach((key) => {
      const value = (obj as any)[key];

      if (typeof value === "string" && !Number.isNaN(Date.parse(value))) {
        (obj as any)[key] = new Date(value);
      }
    });
  }

  public static getDaysOfCurrentWeek(
    year: number,
    monthIndex: number,
    weekIndex: number,
  ): Map<number, number | null> {
    const daysMap = new Map();

    // Créer un objet Date pour le premier jour du mois
    const firstDayOfMonth = new Date(year, monthIndex, 1);

    // Trouver le premier jour du mois et le premier lundi
    const firstDayOfWeek = firstDayOfMonth.getDay(); // 0 = dimanche, 1 = lundi, ..., 6 = samedi
    const firstMondayOffset = (1 - firstDayOfWeek + 7) % 7; // Déterminer le décalage vers le premier lundi
    const firstMonday = new Date(firstDayOfMonth);
    firstMonday.setDate(firstDayOfMonth.getDate() + firstMondayOffset);

    // Calculer le lundi de la semaine demandée (index basé sur 0, donc on ajoute 1)
    const startOfWeek = new Date(firstMonday);
    startOfWeek.setDate(firstMonday.getDate() + (weekIndex - 1) * 7); // Semaine indexée à partir de 0

    // Remplir la Map avec les jours de la semaine (lundi à dimanche)
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(startOfWeek);
      currentDay.setDate(startOfWeek.getDate() + i);

      // Si le jour est dans le même mois, l'ajouter à la Map
      if (currentDay.getMonth() === monthIndex) {
        daysMap.set(i, currentDay.getDate()); // clé: index du jour (0 = lundi), valeur: numéro du jour
      } else {
        // Si le jour dépasse le mois, ajouter null ou une valeur indiquant qu'il n'est pas valide
        daysMap.set(i, null); // Indiquer que ce jour n'est pas dans le mois
      }
    }

    return daysMap;
  }

  public static getFirstDateOfWeek(year: number, monthIndex: number, weekIndex: number): Date {
    // Créer un objet Date pour le premier jour du mois
    const firstDayOfMonth = new Date(year, monthIndex, 1);

    // Obtenir le jour de la semaine du premier jour du mois (0 = dimanche, 6 = samedi)
    const firstDayOfWeek = firstDayOfMonth.getDay();

    // Calculer le décalage pour atteindre le premier lundi du mois
    const firstMondayOffset = (1 - firstDayOfWeek + 7) % 7;

    // Calculer la date du premier lundi
    const firstMonday = new Date(year, monthIndex, 1 + firstMondayOffset);

    // Calculer le lundi de la semaine demandée (index basé sur 0, donc on ajoute 1)
    const startOfWeek = new Date(firstMonday);
    startOfWeek.setDate(firstMonday.getDate() + (weekIndex - 1) * 7); // Semaine indexée à partir de 0

    return startOfWeek;
  }

  public static getLastDateOfWeek(date: Date): Date {
    const endOfWeekDate = new Date(date);
    endOfWeekDate.setDate(date.getDate() + 6); // Ajoute 6 jours pour obtenir la fin de la semaine

    return endOfWeekDate; // Retourne la date de fin de semaine ajustée
  }

  public static fromTimeZone(timeZone: string): Date {
    const zonedDate = utcToZonedTime(new Date(), timeZone);

    const offsetInMinutes = zonedDate.getTimezoneOffset();
    const offset = -offsetInMinutes;

    zonedDate.setMinutes(zonedDate.getMinutes() + offset);

    return zonedDate;
  }

  public static calculateOffsetInMinutes(timezone1: string, timezone2: string) {
    const dateInTimezone1 = UDate.fromTimeZone(timezone1);
    const dateInTimezone2 = UDate.fromTimeZone(timezone2);

    const _timeDifference = dateInTimezone2.getTime() - dateInTimezone1.getTime();

    return Math.floor(_timeDifference / (1000 * 60));
  }

  public static toISOStringWithoutTimeZone(date: Date): string {
    return date.toISOString().replace("Z", "");
  }

  public static minutesToHours(minutes: number) {
    const totalSeconds = minutes * 60;
    const hour = Math.floor(totalSeconds / 3600);
    const minute = Math.floor((totalSeconds % 3600) / 60);
    const second = totalSeconds % 60;

    return { hour, minute, second };
  }

  public static formatToDayMonthYearHMHM(date: Date, duration: number, lang: string) {
    const month = date.getUTCMonth();
    const year = date.getUTCFullYear();
    const firstDayOfMonth = new Date(Date.UTC(year, month, 1)).getUTCDay();
    const adjustedFirstDay = (firstDayOfMonth + 6) % 7;
    const day = date.getUTCDate();
    const dayIndex = (adjustedFirstDay + day - 1) % 7;
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();

    const totalMinutes = hours * 60 + minutes;

    return `${getDay(dayIndex, lang)}, ${day} ${getCalendarMonths(lang)[month]} ${year} (${UTime.format_HH_MM(totalMinutes)} - ${UTime.format_HH_MM(totalMinutes + duration * 60)})`;
  }

  public static formatToDayMonthYear(date: Date, lang: string) {
    const month = date.getUTCMonth();
    const year = date.getUTCFullYear();
    const firstDayOfMonth = new Date(Date.UTC(year, month, 1)).getUTCDay();
    const adjustedFirstDay = (firstDayOfMonth + 6) % 7;
    const day = date.getUTCDate();
    const dayIndex = (adjustedFirstDay + day - 1) % 7;

    return `${getDay(dayIndex, lang).toLowerCase()} ${day} ${getCalendarMonths(lang)[month].toLowerCase()} ${year}`;
  }

  /*

	public static formatToTimeSlots(date: Date, duration: number, lang?: string) {
		const hours = date.getUTCHours();
		const minutes = date.getUTCMinutes();

		const totalMinutes = (hours * 60) + minutes;

		return `${ UTime.format_HH_MM(totalMinutes) } ${ lang ? T(TKey.TO, lang) : '-' } ${ UTime.format_HH_MM(totalMinutes + duration * 60) }`;
	}

	 */

  public static formatToDDMMYYYYHHMM(date: Date) {
    const pad = (n: number) => n.toString().padStart(2, "0");

    const day = pad(date.getUTCDate());
    const month = pad(date.getUTCMonth() + 1);
    const year = date.getUTCFullYear();
    const hours = pad(date.getUTCHours());
    const minutes = pad(date.getUTCMinutes());

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  public static formatToDDMMYYYY(date: Date) {
    const pad = (n: number) => n.toString().padStart(2, "0");

    const day = pad(date.getUTCDate());
    const month = pad(date.getUTCMonth() + 1);
    const year = date.getUTCFullYear();

    return `${day}/${month}/${year}`;
  }

  public static isSameDay(date1: Date, date2: Date) {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  }

  public static formatToHHMM(date: Date) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  public static timeAgo(date: Date, lang: string = "fr"): string {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

    const translations: Record<
      string,
      {
        ago: string;
        justNow: string;
        units: Record<string, string[]>;
      }
    > = {
      fr: {
        ago: "il y a",
        justNow: "à l'instant",
        units: {
          year: ["an", "ans"],
          month: ["mois", "mois"],
          week: ["semaine", "semaines"],
          day: ["jour", "jours"],
          hour: ["heure", "heures"],
          minute: ["minute", "minutes"],
          second: ["seconde", "secondes"],
        },
      },
      en: {
        ago: "ago",
        justNow: "just now",
        units: {
          year: ["year", "years"],
          month: ["month", "months"],
          week: ["week", "weeks"],
          day: ["day", "days"],
          hour: ["hour", "hours"],
          minute: ["minute", "minutes"],
          second: ["second", "seconds"],
        },
      },
    };

    const t = translations[lang] ?? translations.fr;

    const intervals = [
      { key: "year", seconds: 31536000 },
      { key: "month", seconds: 2592000 },
      { key: "week", seconds: 604800 },
      { key: "day", seconds: 86400 },
      { key: "hour", seconds: 3600 },
      { key: "minute", seconds: 60 },
      { key: "second", seconds: 1 },
    ] as const;

    for (const interval of intervals) {
      const count = Math.floor(seconds / interval.seconds);
      if (count >= 1) {
        const unit = count > 1 ? t.units[interval.key][1] : t.units[interval.key][0];

        return lang === "fr" ? `${t.ago} ${count} ${unit}` : `${count} ${unit} ${t.ago}`;
      }
    }

    return t.justNow;
  }
}
